// What the cadence cell shows for one row.
//
// One decision in one place, for the same reason writeResponse.js is its own
// file: this has more edge cases than a table cell should contain, and every
// one of them is a sentence a person reads about their own inbox.
//
// THE SERVER SENDS VALUES, NOT PROSE (Mark + me, 2026-09-25). `cadence`,
// `cadenceDefault` and the entries of `cadences` are 'daily' | 'weekly' | null
// and never English, so the words live here and can change without a backend
// deploy. `cadenceDefault` is defined as "the cadence that applies to this
// person and this source when their own cadence is null, resolved through
// whatever layers exist" - so if a company-level default ever lands, its VALUE
// changes and its MEANING does not, and this file needs no edit.
//
// `cadences` IS THE CONTRACT, NOT A HINT. Never infer whether a mail can be
// scheduled from its label, its `when` text, or the word digest appearing in
// its source. If the engine cannot honour an option, it must not be offered -
// a picker the sender ignores is a silent degradation, and rendering one is
// how this feature would lie.

export const LABELS = { daily: 'Daily', weekly: 'Weekly' };

function label(value) {
  return LABELS[value] || String(value || '');
}

// Sentence for a mail with no schedule to choose. Empty `cadences` means the
// sender fires on an event; ONE entry means it has a schedule but no choice.
// Those are different facts and a person can act on the difference, so they do
// not share a sentence.
function fixedText(cadences) {
  if (!cadences.length) return 'Sent as it happens';
  return `Sent ${label(cadences[0]).toLowerCase()}`;
}

// Returns what to render. `picker` false means show `text` and nothing else.
export function cadenceCell(row) {
  const r = row || {};
  const cadences = Array.isArray(r.cadences) ? r.cadences.filter((c) => !!c) : [];
  const stored = r.cadence || null;
  const fallback = r.cadenceDefault || null;
  // cadenceApplies is the server saying whether the STORED value is still one
  // the engine would honour. Absent means nothing stored to contradict, so it
  // is only ever a problem when it is explicitly false.
  const applies = r.cadenceApplies !== false;

  // A stored choice the engine no longer honours. Both halves of the sentence,
  // because the person needs to know what they picked AND that it stopped
  // counting - telling them only one is how a setting disappears quietly.
  const strandedNote = (!applies && stored)
    ? (cadences.length
      ? `You chose ${label(stored)}. This email is now sent ${cadences.map((c) => label(c).toLowerCase()).join(' or ')}, so that no longer applies.`
      : `You chose ${label(stored)}. This email is now sent as it happens, so that no longer applies.`)
    : '';

  // Fewer than two options is nothing to choose between.
  if (cadences.length < 2) {
    return { picker: false, options: [], value: null, isDefault: false, text: fixedText(cadences), note: strandedNote };
  }

  // The effective value: their own choice when the engine still honours it,
  // otherwise the resolved default. Never the stranded value - that would put a
  // dropdown on a setting the sender ignores.
  const effective = (stored && applies) ? stored : fallback;
  return {
    picker: true,
    options: cadences.map((c) => ({ value: c, label: label(c) })),
    // Null when there is no stored choice and no resolvable default: the picker
    // shows nothing selected rather than inventing Daily, because guessing here
    // would tell someone their mail is on a schedule nobody has set.
    value: effective && cadences.includes(effective) ? effective : null,
    // Whether what is shown is theirs or inherited, so the tab can say so.
    isDefault: !(stored && applies),
    text: '',
    note: strandedNote,
  };
}

// WHETHER THE COLUMN EXISTS AT ALL, so this can be wired before the server
// sends any of these fields. Today every row arrives without `cadences`, every
// cell resolves to "no picker", and an always-empty column would be a visible
// change for no feature. Deciding it from the DATA means the column appears the
// day the API starts answering and not before, with no second deploy and no
// flag to remember to turn on.
export function showCadenceColumn(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.some((r) => {
    const cell = cadenceCell(r);
    // A stranded choice has to be visible even where there is nothing to pick,
    // or the one person who most needs telling is the one who sees nothing.
    return cell.picker || !!cell.note;
  });
}

// Said once, next to the picker, and true in both directions: daily to weekly
// waits for the next send, and a mail switched on after its hour has passed
// still goes today, exactly like any newly enabled recipient.
export const CADENCE_FOOTNOTE = 'Applies to future sends.';

export default cadenceCell;
